import { Modal } from "@mantine/core"
import { Button, Popover, Tooltip } from "@shopify/polaris"
import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
	useRemoveSavedStore,
	useSetSelectedStore,
} from "../hooks/useFirestoreUserHooks.js"
import { GlobalStateContext } from "./providers/GlobalStateProvider.jsx"

export const StoreSelector = ({ independent, disabled }) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const setSelectedStore = useSetSelectedStore()
	const navigate = useNavigate()
	const removeSavedStore = useRemoveSavedStore()

	const [showPopover, setShowPopover] = useState(false)
	const [showRemoveSavedStoreDialog, setShowRemoveSavedStoreDialog] =
		useState(null)

	const stores = Object.keys(state.stores)

	const changeSelectedStore = async (store) => {
		if (store) {
			await setSelectedStore(state.userId, store)

			window.location.href = "/"
		}
	}

	const getShopName = (shop) => {
		if (state.storeNames[shop]) {
			return state.storeNames[shop]
		} else {
			return shop
		}
	}

	const togglePopover = () => {
		if (disabled) return
		setShowPopover(!showPopover)
	}

	const _removeSavedStore = (store) => {
		removeSavedStore(state.userId, store)
			.then(() => {
				window.location.reload()
			})
			.catch((err) => console.error(err))
	}

	const showRemoveSavedStore = (e, store) => {
		e.stopPropagation()
		setShowPopover(false)
		setShowRemoveSavedStoreDialog(store)
	}

	return (
		<div
			style={{
				background: "#cbffb2",
				borderRadius: "5rem",
				position: "relative",
				padding: independent
					? "0.2rem 0.7rem"
					: "0.15rem 0.7rem 0.15rem 2.5rem",
				marginLeft: independent ? "0rem" : "-2.5rem",
				pointerEvents: disabled ? "none" : "auto",
			}}
		>
			<div
				onClick={togglePopover}
				style={{
					color: "#215109",
					cursor: "pointer",
					verticalAlign: "middle",
					height: "1.75rem",
					display: "flex",
					gap: "0.4rem",
					alignItems: "center",
				}}
			>
				<svg width="1rem" height="1rem" viewBox="0 0 24 24">
					<path
						fill="currentColor"
						d="m15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136s-1.275-1.274-1.439-1.411a.384.384 0 0 0-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141c0 1.232 3.24 1.715 3.24 4.629c0 2.295-1.44 3.76-3.406 3.76c-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066a.944.944 0 0 0 .975-.932c0-1.619-2.654-1.694-2.654-4.359c-.034-2.237 1.571-4.416 4.827-4.416c1.257 0 1.875.361 1.875.361l-.945 2.715l-.02.01zM11.17.83c.136 0 .271.038.405.135c-.984.465-2.064 1.639-2.508 3.992a62.82 62.82 0 0 1-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736c.466-1.777 1.333-2.645 2.085-2.971c.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755c-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0C8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674c-.675.213-.694.232-.772.87c-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z"
					></path>
				</svg>
				<span
					className="store"
					style={{
						fontSize: "0.85rem",
						fontWeight: "500",
						flex: "1",
						overflow: "hidden",
						textWrap: "nowrap",
						textOverflow: "ellipsis",
					}}
				>
					{getShopName(state.shop) || t("other.no_stores_linked")}
				</span>

				<Popover
					active={showPopover}
					onClose={() => setShowPopover(false)}
					preferredPosition="below"
					preferredAlignment="right"
					zIndexOverride="510"
					activator={
						<div
							style={{
								cursor: "pointer",
								background: "transparent",
								display: "flex",
								alignItems: "center",
							}}
							onClick={() => setShowPopover(!showPopover)}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								style={{ opacity: disabled ? "0.3" : "1" }}
								width="1rem"
								height="1rem"
								viewBox="0 0 24 24"
							>
								<g fill="currentColor">
									<path d="M11.293 4.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L12 6.414L8.707 9.707a1 1 0 0 1-1.414-1.414l4-4zm-4 10a1 1 0 0 1 1.414 0L12 17.586l3.293-3.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414z"></path>
								</g>
							</svg>
						</div>
					}
				>
					<div
						style={{
							borderRadius: "0.5rem",
							padding: "0.2rem",
							display: "flex",
							flexDirection: "column",
							gap: "0.2rem",
						}}
					>
						{[...stores, ...state.savedStores].map((store) => {
							return (
								<div
									className="select-store-button"
									key={store}
									onClick={() => changeSelectedStore(store)}
									style={{
										padding: "0.3rem 0.5rem",
										display: "flex",
										alignItems: "center",
										gap: "0.6rem",
										borderRadius: "0.3rem",
										cursor: "pointer",
										color: "#444",
									}}
								>
									<div style={{ position: "relative" }}>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="1.2rem"
											height="1.2rem"
											viewBox="0 0 24 24"
										>
											<path
												fill="currentColor"
												d="m15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136s-1.275-1.274-1.439-1.411a.384.384 0 0 0-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141c0 1.232 3.24 1.715 3.24 4.629c0 2.295-1.44 3.76-3.406 3.76c-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066a.944.944 0 0 0 .975-.932c0-1.619-2.654-1.694-2.654-4.359c-.034-2.237 1.571-4.416 4.827-4.416c1.257 0 1.875.361 1.875.361l-.945 2.715l-.02.01zM11.17.83c.136 0 .271.038.405.135c-.984.465-2.064 1.639-2.508 3.992a62.82 62.82 0 0 1-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736c.466-1.777 1.333-2.645 2.085-2.971c.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755c-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0C8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674c-.675.213-.694.232-.772.87c-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z"
											></path>
										</svg>
										{state.staffForStores.includes(
											store,
										) && (
											<div
												style={{
													position: "absolute",
													bottom: "-0.1rem",
													right: "-0.3rem",
												}}
											>
												<Tooltip
													content={
														<div
															style={{
																padding:
																	"0.2rem 0.4rem",
															}}
														>
															<span
																style={{
																	fontSize:
																		"0.8rem",
																	color: "#222",
																}}
															>
																{t(
																	"other.you_are_a_staff_for_this_store",
																)}
															</span>
														</div>
													}
												>
													<div
														style={{
															height: "0.8rem",
															width: "0.8rem",
														}}
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															style={{
																display:
																	"block",
																background:
																	"#ccc",
																color: "#111",
																borderRadius:
																	"0.2rem",
																padding:
																	"0.1rem",
															}}
															width="0.8rem"
															height="0.8rem"
															viewBox="0 0 16 16"
														>
															<path
																fill="currentColor"
																d="M11 1a4 4 0 0 0-3.896 4.91l-5.051 5.235a1.986 1.986 0 0 0 2.842 2.774l5.007-5.072a4.002 4.002 0 0 0 5.062-4.382a.5.5 0 0 0-.85-.287L12 6.293L9.707 4l2.115-2.115a.5.5 0 0 0-.287-.85A4.032 4.032 0 0 0 10.999 1"
															></path>
														</svg>
													</div>
												</Tooltip>
											</div>
										)}
										{state.savedStores.includes(store) && (
											<div
												style={{
													position: "absolute",
													bottom: "-0rem",
													right: "-0.5rem",
												}}
											>
												<Tooltip
													content={
														<div
															style={{
																padding:
																	"0.2rem 0.4rem",
															}}
														>
															<span
																style={{
																	fontSize:
																		"0.8rem",
																	color: "#222",
																}}
															>
																Unfinished setup
															</span>
														</div>
													}
												>
													<div
														style={{
															height: "1rem",
															width: "1rem",
														}}
													>
														<svg
															style={{
																color: "#ff1818",
																display:
																	"block",
															}}
															width="1rem"
															height="1rem"
															viewBox="0 0 512 512"
														>
															<path
																fill="currentColor"
																d="M449.07 399.08L278.64 82.58c-12.08-22.44-44.26-22.44-56.35 0L51.87 399.08A32 32 0 0 0 80 446.25h340.89a32 32 0 0 0 28.18-47.17m-198.6-1.83a20 20 0 1 1 20-20a20 20 0 0 1-20 20m21.72-201.15l-5.74 122a16 16 0 0 1-32 0l-5.74-121.95a21.73 21.73 0 0 1 21.5-22.69h.21a21.74 21.74 0 0 1 21.73 22.7Z"
															></path>
														</svg>
													</div>
												</Tooltip>
											</div>
										)}
									</div>
									<div>
										<div
											style={{
												fontSize: "0.85rem",
												lineHeight: "1.2",
												fontWeight: "500",
												overflow: "hidden",
												whiteSpace: "nowrap",
												textOverflow: "ellipsis",
											}}
										>
											{getShopName(store)}
										</div>
										{state.storeNames[store] && (
											<div
												style={{
													fontSize: "0.7rem",
													lineHeight: "1.2",
													overflow: "hidden",
													whiteSpace: "nowrap",
													textOverflow: "ellipsis",
												}}
											>
												{store}
											</div>
										)}
									</div>
									{store === state.shop && (
										<div
											style={{
												marginLeft: "auto",
												width: "0.5rem",
												height: "0.5rem",
												background: "#526FFB",
												borderRadius: "100%",
											}}
										></div>
									)}
									{state.savedStores.includes(store) && (
										<div
											className="trash-button"
											onClick={(e) =>
												showRemoveSavedStore(e, store)
											}
											style={{
												padding: "0.2rem",
												borderRadius: "0.5rem",
												cursor: "pointer",
												// marginLeft: "auto",
												...(store !== state.shop
													? { marginLeft: "auto" }
													: {}),
											}}
										>
											<svg
												style={{
													height: "1rem",
													width: "1rem",
													display: "block",
												}}
												viewBox="0 0 256 256"
											>
												<path
													fill="currentColor"
													d="M216 48h-36V36a28 28 0 0 0-28-28h-48a28 28 0 0 0-28 28v12H40a12 12 0 0 0 0 24h4v136a20 20 0 0 0 20 20h128a20 20 0 0 0 20-20V72h4a12 12 0 0 0 0-24M100 36a4 4 0 0 1 4-4h48a4 4 0 0 1 4 4v12h-56Zm88 168H68V72h120Zm-72-100v64a12 12 0 0 1-24 0v-64a12 12 0 0 1 24 0m48 0v64a12 12 0 0 1-24 0v-64a12 12 0 0 1 24 0"
												></path>
											</svg>
										</div>
									)}
								</div>
							)
						})}

						<div
							onClick={() => navigate("/savestore")}
							className="link-store-button"
							style={{
								margin: "0.3rem",
								padding: "0.3rem 0.5rem",
								display: "flex",
								alignItems: "center",
								gap: "0.3rem",
								borderRadius: "0.5rem",
								cursor: "pointer",
								justifyContent: "center",
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="1.1em"
								height="1.1rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M18 13h-5v5c0 .55-.45 1-1 1s-1-.45-1-1v-5H6c-.55 0-1-.45-1-1s.45-1 1-1h5V6c0-.55.45-1 1-1s1 .45 1 1v5h5c.55 0 1 .45 1 1s-.45 1-1 1z"
								></path>
							</svg>
							<span
								style={{
									fontSize: "0.9rem",
									fontWeight: "500",
								}}
							>
								{t("other.add_store")}
							</span>
						</div>
					</div>
				</Popover>
			</div>

			<Modal
				opened={showRemoveSavedStoreDialog !== null}
				title={t("components.store_selector.remove_saved_store")}
				withCloseButton
				onClose={() => setShowRemoveSavedStoreDialog(null)}
				radius="lg"
				zIndex={520}
			>
				<p
					style={{
						fontSize: "0.9rem",
						color: "#333",
						marginTop: "1rem",
						marginBottom: "1rem",
					}}
				>
					{t(
						"components.store_selector.do_you_wish_to_remove_the_following_store_from_your_account",
					)}
				</p>
				<p
					style={{
						fontSize: "0.95rem",
						color: "#333",
						fontWeight: "600",
						marginTop: "1rem",
						marginBottom: "1rem",
						textAlign: "center",
					}}
				>
					{showRemoveSavedStoreDialog}
				</p>
				<Button
					fullWidth
					destructive
					onClick={() =>
						_removeSavedStore(showRemoveSavedStoreDialog)
					}
				>
					{t("common.remove")}
				</Button>
			</Modal>

			<style>{`
				.select-store-button {
					color: #555;
					min-height: 2.3rem;
				}
				.select-store-button:hover {
					background: #e7e7e7;
					color: #585858;
				}
				.link-store-button {
					color: #555;
					border: 1px solid #ccc;
				}
				.link-store-button:hover {
					background: #5371FF;
					color: #fff;
					border: 1px solid #5371FF;
				}
				.store {
					display: block;
				}
				@media (max-width: 720px) {
					.store {
						display: none;
					}
				}

				.trash-button:hover {
					background: #f00;
					color: #fff;
				}
			`}</style>
		</div>
	)
}
